import React, { useEffect, useState } from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { userCredentials, snackbarData } from "recoilState/GlobalState";
import { Chip, CircularProgress, Tooltip } from "@mui/material";
import PersonOutlineRounded from "@mui/icons-material/PersonOutlineRounded";
import AssignmentOutlined from "@mui/icons-material/AssignmentOutlined";
import FingerprintRounded from "@mui/icons-material/FingerprintRounded";
import { RequestServiceGet } from "services/services-mia/RequestServiceGet";
import { usuarioFotoPerfilGet } from "services/services-mia";
import { transformDate, transformDateTime, formatDNI } from "services/Local-services";
import { SEMAFORO_COLORS, ESTADO_LABELS } from "pages/migra/utils/usuarioPaso";
import Person from "assets/img/silueta.png";
import styles from "./DetalleModal.module.scss";

const TIPO_CHIP = {
    E: { label: "Entrada", color: "#166534", background: "#f0fdf4", border: "#86efac" },
    S: { label: "Salida", color: "#991b1b", background: "#fef2f2", border: "#fca5a5" },
};

const ESTADO_CHIP_COLORS = {
    P: { color: "#374151", background: "#f3f4f6", border: "#d1d5db" },
    V: { color: "#991b1b", background: "#fef2f2", border: "#fca5a5" },
    C: { color: "#166534", background: "#f0fdf4", border: "#86efac" },
    A: { color: "#92400e", background: "#fffbeb", border: "#fcd34d" },
    B: { color: "#374151", background: "#f3f4f6", border: "#d1d5db" },
};

// TODO ISO 27001: mismo criterio de tenant que Sign/index.jsx; extraer a un
// helper compartido si aparece una tercera copia.
function getTenantName() {
    const origin = window.location.origin;
    if (origin === "https://clientes.miaid.me") return "evan-prod";
    if (origin === "https://clientes-dev.miaid.me") return "evan-dev";
    if (origin === "https://clientes-test.miaid.me") return "evan-test";
    return "evan-dev";
}

function EstadoChip({ estado }) {
    if (!estado) return <span className={styles.value}>-</span>;
    const palette = ESTADO_CHIP_COLORS[estado] ?? ESTADO_CHIP_COLORS.B;
    return (
        <Chip
            label={ESTADO_LABELS[estado] ?? estado}
            size="small"
            sx={{
                color: palette.color,
                backgroundColor: palette.background,
                border: `1px solid ${palette.border}`,
                fontWeight: 600,
            }}
        />
    );
}

function TipoChip({ tipo }) {
    if (!tipo) return <span className={styles.value}>-</span>;
    const chip = TIPO_CHIP[tipo] ?? { label: tipo, color: "#374151", background: "#f3f4f6", border: "#d1d5db" };
    return (
        <Chip
            label={chip.label}
            size="small"
            sx={{
                color: chip.color,
                backgroundColor: chip.background,
                border: `1px solid ${chip.border}`,
                fontWeight: 600,
            }}
        />
    );
}

function SemaforoValue({ semaforo }) {
    const color = SEMAFORO_COLORS[semaforo];
    if (!color) return <span className={styles.value}>-</span>;
    return <span className={styles.semaforoDot} style={{ backgroundColor: color }} />;
}

function DataRows({ rows }) {
    return (
        <table className={styles.data}>
            <tbody>
                {rows.map(([label, value]) => (
                    <tr key={label}>
                        <td className={styles.label}>{label}</td>
                        <td>{value ?? <span className={styles.value}>-</span>}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

function Foto({ src, alt, fallbackIcon }) {
    return (
        <div className={styles.photo}>
            {src ? (
                <img src={src} alt={alt} />
            ) : (
                <div className={styles.photoEmpty}>{fallbackIcon}</div>
            )}
        </div>
    );
}

export default function DetalleModal({ row }) {
    const credentials = useRecoilValue(userCredentials);
    const setSnackbarInfo = useSetRecoilState(snackbarData);

    const [autenticacion, setAutenticacion] = useState(null);
    const [fotoPerfil, setFotoPerfil] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        let active = true;

        const cargarDetalle = async () => {
            setIsLoading(true);

            const [autenticacionResult, fotoPerfilResult] = await Promise.all([
                row.migraUsuarioPasoAutenticacionId
                    ? RequestServiceGet(
                        {
                            service: "SBMAutenticacionGet",
                            params: {
                                tenantNombre: getTenantName(),
                                autenticacionId: row.migraUsuarioPasoAutenticacionId,
                            },
                        },
                        credentials,
                        setSnackbarInfo
                    )
                    : Promise.resolve(null),
                row.usuarioId
                    ? usuarioFotoPerfilGet(row.usuarioId, credentials)
                    : Promise.resolve(null),
            ]);

            if (!active) return;
            setAutenticacion(autenticacionResult?.[0] ?? null);
            setFotoPerfil(
                typeof fotoPerfilResult === "string"
                    ? fotoPerfilResult
                    : fotoPerfilResult?.imagePathPERFIL ?? null
            );
            setIsLoading(false);
        };

        cargarDetalle();

        return () => { active = false; };
    }, [row, credentials, setSnackbarInfo]);

    if (isLoading) {
        return (
            <div style={{ display: "flex", justifyContent: "center", padding: "3rem" }}>
                <CircularProgress />
            </div>
        );
    }

    const genero = row.usuarioSexo === "M"
        ? "Masculino"
        : row.usuarioSexo === "F"
            ? "Femenino"
            : row.usuarioSexo
                ? "No Binario"
                : null;

    const tipoDeDocumento = row.tipoDeDocumentoCod || row.tipoDeDocumentoNombre
        ? [row.tipoDeDocumentoCod, row.tipoDeDocumentoNombre].filter(Boolean).join(" - ")
        : null;

    return (
        <div className={styles.container}>
            <section className={styles.section}>
                <h4 className={styles.sectionTitle}>
                    <PersonOutlineRounded /> Datos del usuario
                </h4>
                <div className={styles.body}>
                    <Foto
                        src={fotoPerfil || Person}
                        alt="foto de perfil"
                        fallbackIcon={<PersonOutlineRounded />}
                    />
                    <DataRows rows={[
                        ["Apellido", row.usuarioApellidos],
                        ["Nombre", row.usuarioNombres],
                        ["Tipo de documento", tipoDeDocumento],
                        ["Número de documento", row.usuarioNroDeDocumentoX ? formatDNI(row.usuarioNroDeDocumentoX) : null],
                        ["Género", genero],
                        ["Fecha de nacimiento", row.usuarioFechaDeNacimiento ? transformDate(row.usuarioFechaDeNacimiento) : null],
                    ]} />
                </div>
            </section>

            <div className={styles.sectionsRow}>
                <section className={styles.section}>
                    <h4 className={styles.sectionTitle}>
                        <AssignmentOutlined /> Datos del registro
                    </h4>
                    <DataRows rows={[
                        ["Paso", row.migraPasoNombre],
                        ["Tipo", <TipoChip tipo={row.migraUsuarioPasoTipo} />],
                        ["Estado", <EstadoChip estado={row.migraUsuarioPasoEstado} />],
                        ["Semáforo", <SemaforoValue semaforo={row.migraUsuarioPasoSemaforo} />],
                        ["Fecha creación", row.migraUsuarioPasoFechaCreacion ? transformDateTime(row.migraUsuarioPasoFechaCreacion) : null],
                    ]} />
                </section>

                <section className={styles.section}>
                    <h4 className={styles.sectionTitle}>
                        <FingerprintRounded /> Datos del evento
                    </h4>
                    <div className={styles.body}>
                        <Tooltip title={autenticacion?.autenticacionImagenURL ? "" : "Sin imagen de autenticación"}>
                            <span>
                                <Foto
                                    src={autenticacion?.autenticacionImagenURL}
                                    alt="imagen de autenticación"
                                    fallbackIcon={<FingerprintRounded />}
                                />
                            </span>
                        </Tooltip>
                        <DataRows rows={[
                            ["Fecha check-in", row.migraUsuarioPasoFechaCheckin ? transformDateTime(row.migraUsuarioPasoFechaCheckin) : null],
                            ["Identificación", row.migraUsuarioPasoIdentificacion],
                            ["Autenticación", row.migraUsuarioPasoAutenticacionId],
                            ["Latitud", row.migraUsuarioPasoLatitud],
                            ["Longitud", row.migraUsuarioPasoLongitud],
                        ]} />
                    </div>
                </section>
            </div>
        </div>
    );
}
